/**
 * QuotatoreCatNatPage — Wizard 7 step per richiesta quotazione CAT NAT.
 *
 * Salva stato in localStorage. Al submit chiama POST /api/catnat/richiesta
 * e mostra pagina di conferma con request_id.
 *
 * Nota: SOLO raccolta dati (scelta utente A). Nessun calcolo premio in FE.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, Building2, CheckCircle, Info, MapPin,
  Shield, Zap, FileCheck, Download, AlertTriangle,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LS_KEY = "catnat_wizard_v1";

const DEFAULT_DATA = {
  cliente: {
    ragione_sociale: "", partita_iva: "", codice_fiscale: "", codice_ateco: "",
    forma_giuridica: "srl", fatturato_annuo: 0, numero_dipendenti: 0,
    email: "", telefono: "", pec: "",
    referente: { nome: "", cognome: "", ruolo: "" },
  },
  dati_specifici: {
    ubicazione: {
      indirizzo: "", citta: "", provincia: "", cap: "",
      zona_sismica_dichiarata: "non_so",
      area_rischio_idrogeologico_dichiarata: "non_so",
    },
    ubicazioni_totali: 1,
    fabbricato_abitazione: { presente: false },
    fabbricato_non_abitazione: {
      presente: true, tipo: "non_abitazione",
      vincolo_belle_arti: false,
      tipologia_struttura: "cemento_armato",
      anno_costruzione: "1980_1989",
      certificazione_antisismica: "nessuna",
      intero_edificio: true,
      num_piani_ft: "da_0_a_3",
      piani_interrati: false,
    },
    valori_assicurabili: {
      fabbricato: { somma_assicurata: 0, proprieta_terzi: false, somma_assicurata_terzi: 0 },
      terreno: { somma_assicurata: 0, proprieta_terzi: false, somma_assicurata_terzi: 0, ettari: 0 },
      macchinario_chiuso: { somma_assicurata: 0, proprieta_terzi: false, somma_assicurata_terzi: 0 },
      macchinario_aperto: { somma_assicurata: 0, proprieta_terzi: false, somma_assicurata_terzi: 0 },
      merci: { somma_assicurata: 0, proprieta_terzi: false, somma_assicurata_terzi: 0 },
      macchinari_cantiere: { somma_assicurata: 0, proprieta_terzi: false, somma_assicurata_terzi: 0 },
      impianti_rinnovabili: [],
    },
    garanzie_cat_richieste: {
      terremoto: { attiva: true, scoperto_pct: 10, scoperto_minimo_eur: 5000, limite_indennizzo_pct: 100 },
      alluvione: { attiva: true, scoperto_pct: 10, scoperto_minimo_eur: 5000, limite_indennizzo_pct: 100 },
      frana: { attiva: true, scoperto_pct: 10, scoperto_minimo_eur: 5000, limite_indennizzo_pct: 100 },
    },
    garanzie_atmosferiche_opzionali: { sovraccarico_neve: false, allagamento: false, elementi_fragili: false },
    mitigazioni: { alluvione: false, alluvione_dettaglio: "", frana: false, frana_dettaglio: "" },
  },
  consensi: { privacy: false, commerciale: false },
  note_cliente: "",
};

const STEPS = [
  { id: 1, label: "Azienda", icon: Building2 },
  { id: 2, label: "Ubicazione", icon: MapPin },
  { id: 3, label: "Fabbricato", icon: Building2 },
  { id: 4, label: "Valori", icon: Shield },
  { id: 5, label: "Garanzie CAT", icon: Zap },
  { id: 6, label: "Atmosferiche", icon: Info },
  { id: 7, label: "Conferma", icon: CheckCircle },
];

export default function QuotatoreCatNatPage() {
  const [step, setStep] = useState(1);
  const [data, setData] = useState(() => {
    try { const s = localStorage.getItem(LS_KEY); if (s) return JSON.parse(s); } catch {}
    return structuredClone(DEFAULT_DATA);
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { try { localStorage.setItem(LS_KEY, JSON.stringify(data)); } catch {} }, [data]);

  const merge = (path, val) => {
    setData(prev => {
      const copy = structuredClone(prev);
      let node = copy;
      const parts = path.split(".");
      for (let i = 0; i < parts.length - 1; i++) node = node[parts[i]];
      node[parts[parts.length - 1]] = val;
      return copy;
    });
  };

  const canNext = useMemo(() => {
    if (step === 1) {
      const c = data.cliente;
      return c.ragione_sociale && /^\d{11}$/.test(c.partita_iva) && c.email && c.telefono && c.codice_ateco && c.referente.nome && c.referente.cognome;
    }
    if (step === 2) {
      const u = data.dati_specifici.ubicazione;
      return u.indirizzo && u.citta && /^[A-Za-z]{2}$/.test(u.provincia) && /^\d{5}$/.test(u.cap);
    }
    if (step === 7) return data.consensi.privacy;
    return true;
  }, [step, data]);

  const submit = async () => {
    if (!data.consensi.privacy) { toast.error("Consenso privacy obbligatorio"); return; }
    setSubmitting(true);
    try {
      const { data: res } = await axios.post(`${API}/catnat/richiesta`, data);
      setResult(res);
      try { localStorage.removeItem(LS_KEY); } catch {}
      toast.success("Richiesta inviata con successo");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Errore nell'invio. Riprova.";
      toast.error(typeof msg === "string" ? msg : "Errore validazione dati");
    } finally { setSubmitting(false); }
  };

  // ==============================
  // SCHERMATA DI CONFERMA POST SUBMIT
  // ==============================
  if (result?.ok) {
    return (
      <div className="min-h-screen bg-[#F5F4F0]">
        <Header />
        <section className="max-w-3xl mx-auto px-4 sm:px-6 py-20">
          <div className="bg-white rounded-3xl shadow-lg p-10 text-center border border-emerald-200">
            <div className="w-16 h-16 rounded-full bg-emerald-500 text-white flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8" />
            </div>
            <h1 className="font-heading text-3xl text-[#163321] mb-3">Richiesta ricevuta</h1>
            <p className="text-[#4A5E51] mb-6 leading-relaxed">{result.message}</p>
            <div className="bg-[#F5F4F0] rounded-xl p-4 inline-block mb-6">
              <p className="text-[11px] uppercase tracking-widest text-[#4A5E51] font-bold">ID richiesta</p>
              <p className="font-mono text-sm text-[#0F1F14] mt-1" data-testid="request-id">{result.request_id}</p>
            </div>
            <div className="flex flex-wrap gap-3 justify-center">
              <Link to="/aziende" className="px-5 py-2.5 rounded-full bg-[#163321] text-white text-sm font-semibold hover:bg-[#0F2F1F]">Torna alle polizze Aziende</Link>
              <Link to="/" className="px-5 py-2.5 rounded-full border border-[#163321]/30 text-[#163321] text-sm font-semibold hover:bg-[#163321]/5">Home</Link>
            </div>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F4F0]">
      <Header />
      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
        <div className="mb-6">
          <div className="inline-flex items-center gap-2 bg-amber-500 text-white text-[10px] uppercase tracking-widest font-bold px-3 py-1 rounded-full mb-3">
            <AlertTriangle className="w-3 h-3" /> Obbligo L. 213/2023
          </div>
          <h1 className="font-heading text-3xl text-[#163321] mb-1">Quotatore CAT NAT</h1>
          <p className="text-sm text-[#4A5E51]">Compila i dati per ricevere preventivo gratuito in 24h lavorative.</p>
        </div>

        {/* PROGRESS BAR */}
        <div className="mb-8">
          <div className="flex items-center gap-1 overflow-x-auto pb-2">
            {STEPS.map((s, idx) => (
              <div key={s.id} className="flex items-center gap-1 flex-shrink-0">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold ${
                  s.id < step ? "bg-[#163321] text-white" :
                  s.id === step ? "bg-amber-500 text-white ring-4 ring-amber-500/20" :
                  "bg-[#E5E2DB] text-[#4A5E51]"
                }`}>{s.id}</div>
                <span className={`text-[11px] font-semibold hidden sm:inline ${s.id === step ? "text-[#163321]" : "text-[#4A5E51]"}`}>{s.label}</span>
                {idx < STEPS.length - 1 && <div className="w-3 sm:w-6 h-[2px] bg-[#E5E2DB]" />}
              </div>
            ))}
          </div>
        </div>

        {/* CONTENT */}
        <div className="bg-white rounded-2xl border border-[#E5E2DB] p-6" data-testid={`wizard-step-${step}`}>
          {step === 1 && <Step1 data={data} merge={merge} />}
          {step === 2 && <Step2 data={data} merge={merge} />}
          {step === 3 && <Step3 data={data} merge={merge} />}
          {step === 4 && <Step4 data={data} merge={merge} />}
          {step === 5 && <Step5 data={data} merge={merge} />}
          {step === 6 && <Step6 data={data} merge={merge} />}
          {step === 7 && <Step7 data={data} merge={merge} />}
        </div>

        {/* NAVIGATION */}
        <div className="mt-6 flex items-center justify-between">
          <Button variant="outline" onClick={() => setStep(s => Math.max(1, s - 1))} disabled={step === 1} data-testid="wizard-back">
            <ArrowLeft className="w-4 h-4 mr-1" /> Indietro
          </Button>
          {step < 7 ? (
            <Button onClick={() => setStep(s => Math.min(7, s + 1))} disabled={!canNext} className="bg-[#163321] hover:bg-[#0F2F1F]" data-testid="wizard-next">
              Avanti <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button onClick={submit} disabled={submitting || !canNext} className="bg-amber-600 hover:bg-amber-700" data-testid="wizard-submit">
              {submitting ? "Invio in corso…" : "Invia richiesta"}
            </Button>
          )}
        </div>

        <p className="text-[10px] text-[#4A5E51]/60 mt-6 leading-relaxed">
          ⚠️ Quotazione indicativa non vincolante. Il preventivo definitivo verrà rilasciato da un nostro consulente dopo analisi del caso. I dati raccolti sono trattati ai sensi del Regolamento (UE) 2016/679 (GDPR) per la sola finalità della quotazione richiesta e conservati per il tempo strettamente necessario.
        </p>
      </section>
      <Footer />
    </div>
  );
}

// ==================== STEP COMPONENTS ====================
function L({ children }) { return <label className="block text-[11px] font-semibold text-[#163321] mb-1 uppercase tracking-wide">{children}</label>; }
function Row({ children }) { return <div className="grid sm:grid-cols-2 gap-3">{children}</div>; }

function Step1({ data, merge }) {
  const c = data.cliente;
  return (
    <div className="space-y-4">
      <h2 className="font-heading text-xl text-[#163321]">1. Dati impresa</h2>
      <Row>
        <div><L>Ragione sociale *</L><Input value={c.ragione_sociale} onChange={e => merge("cliente.ragione_sociale", e.target.value)} data-testid="q-ragsoc" /></div>
        <div><L>Forma giuridica</L>
          <select value={c.forma_giuridica} onChange={e => merge("cliente.forma_giuridica", e.target.value)} className="w-full h-10 px-3 border border-[#E5E2DB] rounded-md text-sm bg-white">
            <option value="srl">SRL</option><option value="spa">SpA</option><option value="sas">SAS</option><option value="snc">SNC</option>
            <option value="ditta_individuale">Ditta individuale</option><option value="cooperativa">Cooperativa</option><option value="altro">Altro</option>
          </select>
        </div>
      </Row>
      <Row>
        <div><L>Partita IVA (11 cifre) *</L><Input value={c.partita_iva} onChange={e => merge("cliente.partita_iva", e.target.value.replace(/\D/g, "").slice(0, 11))} data-testid="q-piva" /></div>
        <div><L>Codice fiscale (se diverso)</L><Input value={c.codice_fiscale} onChange={e => merge("cliente.codice_fiscale", e.target.value.toUpperCase().slice(0, 16))} /></div>
      </Row>
      <Row>
        <div><L>Codice ATECO (es. 45.20.10) *</L><Input value={c.codice_ateco} onChange={e => merge("cliente.codice_ateco", e.target.value)} placeholder="45.20.10" data-testid="q-ateco" /></div>
        <div><L>N. dipendenti</L><Input type="number" min="0" value={c.numero_dipendenti} onChange={e => merge("cliente.numero_dipendenti", parseInt(e.target.value) || 0)} /></div>
      </Row>
      <div><L>Fatturato annuo (€)</L><Input type="number" min="0" step="1000" value={c.fatturato_annuo} onChange={e => merge("cliente.fatturato_annuo", parseFloat(e.target.value) || 0)} /></div>
      <h3 className="font-heading text-base text-[#163321] pt-3 border-t border-[#E5E2DB]">Referente</h3>
      <Row>
        <div><L>Nome *</L><Input value={c.referente.nome} onChange={e => merge("cliente.referente.nome", e.target.value)} /></div>
        <div><L>Cognome *</L><Input value={c.referente.cognome} onChange={e => merge("cliente.referente.cognome", e.target.value)} /></div>
      </Row>
      <div><L>Ruolo</L><Input value={c.referente.ruolo} onChange={e => merge("cliente.referente.ruolo", e.target.value)} placeholder="Amministratore, CFO, …" /></div>
      <Row>
        <div><L>Email *</L><Input type="email" value={c.email} onChange={e => merge("cliente.email", e.target.value)} data-testid="q-email" /></div>
        <div><L>Telefono *</L><Input type="tel" value={c.telefono} onChange={e => merge("cliente.telefono", e.target.value)} data-testid="q-tel" /></div>
      </Row>
      <div><L>PEC</L><Input value={c.pec} onChange={e => merge("cliente.pec", e.target.value)} /></div>
    </div>
  );
}

function Step2({ data, merge }) {
  const u = data.dati_specifici.ubicazione;
  return (
    <div className="space-y-4">
      <h2 className="font-heading text-xl text-[#163321]">2. Ubicazione principale</h2>
      <div><L>Indirizzo *</L><Input value={u.indirizzo} onChange={e => merge("dati_specifici.ubicazione.indirizzo", e.target.value)} /></div>
      <Row>
        <div><L>Città *</L><Input value={u.citta} onChange={e => merge("dati_specifici.ubicazione.citta", e.target.value)} /></div>
        <div><L>Provincia (2 lett.) *</L><Input value={u.provincia} onChange={e => merge("dati_specifici.ubicazione.provincia", e.target.value.toUpperCase().slice(0, 2))} /></div>
      </Row>
      <Row>
        <div><L>CAP *</L><Input value={u.cap} onChange={e => merge("dati_specifici.ubicazione.cap", e.target.value.replace(/\D/g, "").slice(0, 5))} /></div>
        <div><L>N. ubicazioni totali</L><Input type="number" min="1" value={data.dati_specifici.ubicazioni_totali} onChange={e => merge("dati_specifici.ubicazioni_totali", parseInt(e.target.value) || 1)} /></div>
      </Row>
      <Row>
        <div><L>Zona sismica (se nota)</L>
          <select value={u.zona_sismica_dichiarata} onChange={e => merge("dati_specifici.ubicazione.zona_sismica_dichiarata", e.target.value)} className="w-full h-10 px-3 border border-[#E5E2DB] rounded-md text-sm bg-white">
            <option value="non_so">Non so</option><option value="1">Zona 1 (alta)</option><option value="2">Zona 2</option><option value="3">Zona 3</option><option value="4">Zona 4 (bassa)</option>
          </select>
        </div>
        <div><L>Rischio idrogeologico</L>
          <select value={u.area_rischio_idrogeologico_dichiarata} onChange={e => merge("dati_specifici.ubicazione.area_rischio_idrogeologico_dichiarata", e.target.value)} className="w-full h-10 px-3 border border-[#E5E2DB] rounded-md text-sm bg-white">
            <option value="non_so">Non so</option><option value="alto">Alto</option><option value="medio">Medio</option><option value="basso">Basso</option>
          </select>
        </div>
      </Row>
    </div>
  );
}

function Step3({ data, merge }) {
  const f = data.dati_specifici.fabbricato_non_abitazione;
  return (
    <div className="space-y-4">
      <h2 className="font-heading text-xl text-[#163321]">3. Caratteristiche fabbricato</h2>
      <p className="text-xs text-[#4A5E51]">Inserisci le caratteristiche del fabbricato prevalente (sede operativa principale).</p>
      <Row>
        <div><L>Tipologia struttura portante</L>
          <select value={f.tipologia_struttura} onChange={e => merge("dati_specifici.fabbricato_non_abitazione.tipologia_struttura", e.target.value)} className="w-full h-10 px-3 border border-[#E5E2DB] rounded-md text-sm bg-white" data-testid="q-struttura">
            <option value="cemento_armato">Cemento armato</option><option value="metallo">Metallo</option><option value="pietra_laterizi">Pietra e laterizi</option><option value="legno">Legno</option><option value="materiali_generici">Materiali generici (altro)</option>
          </select>
        </div>
        <div><L>Anno costruzione / ristrutturazione</L>
          <select value={f.anno_costruzione} onChange={e => merge("dati_specifici.fabbricato_non_abitazione.anno_costruzione", e.target.value)} className="w-full h-10 px-3 border border-[#E5E2DB] rounded-md text-sm bg-white">
            <option value="ante_1920">ante 1920</option><option value="1920_1949">1920-1949</option><option value="1950_1959">1950-1959</option><option value="1960_1969">1960-1969</option><option value="1970_1979">1970-1979</option><option value="1980_1989">1980-1989</option><option value="1990_1996">1990-1996</option><option value="dal_1997">dal 1997 in poi</option>
          </select>
        </div>
      </Row>
      <Row>
        <div><L>Certificazione antisismica</L>
          <select value={f.certificazione_antisismica} onChange={e => merge("dati_specifici.fabbricato_non_abitazione.certificazione_antisismica", e.target.value)} className="w-full h-10 px-3 border border-[#E5E2DB] rounded-md text-sm bg-white">
            <option value="nessuna">Nessuna</option><option value="A+">A+</option><option value="A">A</option><option value="B">B</option>
          </select>
        </div>
        <div><L>Numero piani fuori terra</L>
          <select value={f.num_piani_ft} onChange={e => merge("dati_specifici.fabbricato_non_abitazione.num_piani_ft", e.target.value)} className="w-full h-10 px-3 border border-[#E5E2DB] rounded-md text-sm bg-white">
            <option value="da_0_a_3">da 0 a 3</option><option value="oltre_3">oltre 3</option>
          </select>
        </div>
        <div><L>Superficie utile (mq) <span className="text-[#4A5E51] font-normal">(opzionale)</span></L>
          <input type="number" min="10" max="100000" step="1" value={f.superficie_mq ?? ""} onChange={e => merge("dati_specifici.fabbricato_non_abitazione.superficie_mq", e.target.value === "" ? null : Number(e.target.value))} className="w-full h-10 px-3 border border-[#E5E2DB] rounded-md text-sm bg-white" placeholder="es. 350" data-testid="q-superficie-mq" />
        </div>
      </Row>
      <label className="flex items-start gap-2 text-sm"><input type="checkbox" checked={f.piani_interrati} onChange={e => merge("dati_specifici.fabbricato_non_abitazione.piani_interrati", e.target.checked)} className="mt-0.5 accent-[#163321]" />Presenza piani interrati</label>
      <label className="flex items-start gap-2 text-sm"><input type="checkbox" checked={f.vincolo_belle_arti} onChange={e => merge("dati_specifici.fabbricato_non_abitazione.vincolo_belle_arti", e.target.checked)} className="mt-0.5 accent-[#163321]" />Fabbricato soggetto a vincolo Belle Arti</label>
    </div>
  );
}

function Step4({ data, merge }) {
  const v = data.dati_specifici.valori_assicurabili;
  const cat = (key, label) => (
    <div className="bg-[#F5F4F0] rounded-xl p-3">
      <p className="text-[11px] font-bold text-[#163321] uppercase tracking-wide mb-2">{label}</p>
      <Row>
        <div><L>Somma assicurata (€)</L><Input type="number" min="0" value={v[key].somma_assicurata} onChange={e => merge(`dati_specifici.valori_assicurabili.${key}.somma_assicurata`, parseFloat(e.target.value) || 0)} /></div>
        <div><L>Di cui di terzi (€)</L><Input type="number" min="0" value={v[key].somma_assicurata_terzi} onChange={e => merge(`dati_specifici.valori_assicurabili.${key}.somma_assicurata_terzi`, parseFloat(e.target.value) || 0)} /></div>
      </Row>
    </div>
  );
  return (
    <div className="space-y-3">
      <h2 className="font-heading text-xl text-[#163321]">4. Valori da assicurare</h2>
      <p className="text-xs text-[#4A5E51]">Inserisci gli importi di ricostruzione a nuovo / valore a prezzo di mercato per ogni categoria di beni presenti.</p>
      {cat("fabbricato", "Fabbricato")}
      <div className="bg-[#F5F4F0] rounded-xl p-3">
        <p className="text-[11px] font-bold text-[#163321] uppercase tracking-wide mb-2">Terreno</p>
        <Row>
          <div><L>Somma (€)</L><Input type="number" min="0" value={v.terreno.somma_assicurata} onChange={e => merge("dati_specifici.valori_assicurabili.terreno.somma_assicurata", parseFloat(e.target.value) || 0)} /></div>
          <div><L>Ettari</L><Input type="number" min="0" step="0.01" value={v.terreno.ettari} onChange={e => merge("dati_specifici.valori_assicurabili.terreno.ettari", parseFloat(e.target.value) || 0)} /></div>
        </Row>
      </div>
      {cat("macchinario_chiuso", "Macchinario al chiuso")}
      {cat("macchinario_aperto", "Macchinario all'aperto")}
      {cat("merci", "Merci")}
      {cat("macchinari_cantiere", "Macchinari / baraccamenti cantiere")}
    </div>
  );
}

function GaranziaRow({ label, path, data, merge }) {
  const g = data.dati_specifici.garanzie_cat_richieste[path];
  return (
    <div className="bg-[#F5F4F0] rounded-xl p-3 space-y-2">
      <label className="flex items-center gap-2">
        <input type="checkbox" checked={g.attiva} onChange={e => merge(`dati_specifici.garanzie_cat_richieste.${path}.attiva`, e.target.checked)} className="accent-[#163321]" />
        <span className="text-sm font-semibold text-[#0F1F14]">{label}</span>
      </label>
      {g.attiva && (
        <div className="grid grid-cols-3 gap-2 pl-6">
          <div><L>Scoperto %</L>
            <select value={g.scoperto_pct} onChange={e => merge(`dati_specifici.garanzie_cat_richieste.${path}.scoperto_pct`, parseInt(e.target.value))} className="w-full h-9 px-2 border border-[#E5E2DB] rounded-md text-xs bg-white">
              {[0, 5, 10, 15, 20, 30].map(x => <option key={x} value={x}>{x}%</option>)}
            </select>
          </div>
          <div><L>Min. scoperto</L>
            <select value={g.scoperto_minimo_eur} onChange={e => merge(`dati_specifici.garanzie_cat_richieste.${path}.scoperto_minimo_eur`, parseInt(e.target.value))} className="w-full h-9 px-2 border border-[#E5E2DB] rounded-md text-xs bg-white">
              {[0, 5000, 10000, 20000, 50000, 100000].map(x => <option key={x} value={x}>€ {x.toLocaleString("it-IT")}</option>)}
            </select>
          </div>
          <div><L>Limite %</L>
            <select value={g.limite_indennizzo_pct} onChange={e => merge(`dati_specifici.garanzie_cat_richieste.${path}.limite_indennizzo_pct`, parseInt(e.target.value))} className="w-full h-9 px-2 border border-[#E5E2DB] rounded-md text-xs bg-white">
              {[100, 70, 50, 40, 30, 20].map(x => <option key={x} value={x}>{x}%</option>)}
            </select>
          </div>
        </div>
      )}
    </div>
  );
}

function Step5({ data, merge }) {
  return (
    <div className="space-y-4">
      <h2 className="font-heading text-xl text-[#163321]">5. Garanzie catastrofali</h2>
      <p className="text-xs text-[#4A5E51]">Il DM 18/2025 fissa scoperto massimo al 15% e limite indennizzo minimo 100% per somme &lt; €1 mln.</p>
      <GaranziaRow label="Terremoto" path="terremoto" data={data} merge={merge} />
      <GaranziaRow label="Alluvione / Inondazione / Esondazione" path="alluvione" data={data} merge={merge} />
      <GaranziaRow label="Frana" path="frana" data={data} merge={merge} />
    </div>
  );
}

function Step6({ data, merge }) {
  const a = data.dati_specifici.garanzie_atmosferiche_opzionali;
  const m = data.dati_specifici.mitigazioni;
  return (
    <div className="space-y-4">
      <h2 className="font-heading text-xl text-[#163321]">6. Garanzie atmosferiche opzionali</h2>
      <div className="space-y-2">
        {[
          ["sovraccarico_neve", "Sovraccarico neve"],
          ["allagamento", "Allagamento (piogge eccezionali)"],
          ["elementi_fragili", "Elementi fragili (vetrate, cristalli)"],
        ].map(([k, lbl]) => (
          <label key={k} className="flex items-center gap-2 bg-[#F5F4F0] rounded-xl p-3 text-sm">
            <input type="checkbox" checked={a[k]} onChange={e => merge(`dati_specifici.garanzie_atmosferiche_opzionali.${k}`, e.target.checked)} className="accent-[#163321]" />{lbl}
          </label>
        ))}
      </div>
      <h2 className="font-heading text-xl text-[#163321] pt-3 border-t border-[#E5E2DB]">Misure di mitigazione presenti</h2>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={m.alluvione} onChange={e => merge("dati_specifici.mitigazioni.alluvione", e.target.checked)} className="accent-[#163321]" />Infrastrutture mitigazione alluvione</label>
      {m.alluvione && <Input placeholder="Dettaglio (argini, pompe, paratoie…)" value={m.alluvione_dettaglio} onChange={e => merge("dati_specifici.mitigazioni.alluvione_dettaglio", e.target.value)} />}
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={m.frana} onChange={e => merge("dati_specifici.mitigazioni.frana", e.target.checked)} className="accent-[#163321]" />Infrastrutture mitigazione frana</label>
      {m.frana && <Input placeholder="Dettaglio (muri contenimento, drenaggi…)" value={m.frana_dettaglio} onChange={e => merge("dati_specifici.mitigazioni.frana_dettaglio", e.target.value)} />}
    </div>
  );
}

function Step7({ data, merge }) {
  return (
    <div className="space-y-4">
      <h2 className="font-heading text-xl text-[#163321]">7. Conferma e invio</h2>
      <div className="bg-[#F5F4F0] rounded-xl p-4 text-sm space-y-1">
        <p><strong>Impresa:</strong> {data.cliente.ragione_sociale} — P.IVA {data.cliente.partita_iva}</p>
        <p><strong>ATECO:</strong> {data.cliente.codice_ateco}</p>
        <p><strong>Ubicazione:</strong> {data.dati_specifici.ubicazione.indirizzo}, {data.dati_specifici.ubicazione.citta} ({data.dati_specifici.ubicazione.provincia})</p>
        <p><strong>Contatti:</strong> {data.cliente.email} — {data.cliente.telefono}</p>
        <p><strong>Garanzie CAT attive:</strong> {Object.entries(data.dati_specifici.garanzie_cat_richieste).filter(([, v]) => v.attiva).map(([k]) => k).join(", ") || "nessuna"}</p>
      </div>
      <div>
        <L>Note aggiuntive (facoltative)</L>
        <textarea value={data.note_cliente} onChange={e => merge("note_cliente", e.target.value)} rows="3" className="w-full border border-[#E5E2DB] rounded-xl px-3 py-2 text-sm" maxLength={2000} />
      </div>
      <label className="flex items-start gap-2 text-sm bg-[#F5F4F0] p-3 rounded-xl">
        <input type="checkbox" checked={data.consensi.privacy} onChange={e => merge("consensi.privacy", e.target.checked)} className="mt-0.5 accent-[#163321]" data-testid="q-privacy" />
        <span>Ho letto l'<Link to="/privacy" target="_blank" className="text-[#163321] underline">Informativa Privacy</Link> e acconsento al trattamento dei dati per la finalità della quotazione (art. 6.1.b GDPR, esecuzione di misure precontrattuali). *</span>
      </label>
      <label className="flex items-start gap-2 text-sm bg-[#F5F4F0] p-3 rounded-xl">
        <input type="checkbox" checked={data.consensi.commerciale} onChange={e => merge("consensi.commerciale", e.target.checked)} className="mt-0.5 accent-[#163321]" />
        <span>Acconsento a ricevere comunicazioni commerciali su nuovi prodotti e offerte di Colucci Assicuratori (facoltativo).</span>
      </label>
    </div>
  );
}
